import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import {
  Analytics,
  Check,
  Close,
  Diamond,
  Download,
  ExpandMore,
  GridView,
  Inventory2,
  LocalOffer,
  LocalMall,
  LocalShipping,
  Percent,
  PhotoCamera,
  Public,
  ShoppingBag,
  ShoppingCart,
  Storefront,
  Videocam,
} from '@mui/icons-material';

type PlatformKey = 'amazon' | 'tiktok' | 'ozon' | 'ebay' | 'walmart' | 'etsy' | 'temu' | 'aliexpress' | 'shopee';
type StrategyType = 'margin' | 'profit';
type TikTokRegion = 'us' | 'uk' | 'sea';

interface Dimensions {
  l: number;
  w: number;
  h: number;
}

interface PlatformInput {
  purchaseCost: number;
  shippingCost: number;
  packagingCost: number;
  otherCost: number;
  shippingCharged: number;
  weight: number;
  dims: Dimensions;
  exchangeRate: number;
  targetMargin: number;
  targetProfit: number;
  categoryId?: string;
  referralRate?: number;
  referralMinFee?: number;
  closingFee?: number;
  sellingPlan?: 'individual' | 'professional';
  sellingPlanFee?: number;
  fulfillmentMode?: 'fba' | 'fbm';
  fulfillmentFee?: number;
  storageFee?: number;
  inboundShippingFee?: number;
  fbmHandlingFee?: number;
  platformDiscount?: number;
  tax?: number;
  sellerDiscount?: number;
  affiliateRate?: number;
  affiliatePartnerRate?: number;
  adsRate?: number;
  tiktokRegion?: TikTokRegion;
  tiktokShippingType?: 'platform' | 'seller' | 'fbt';
  fbtFulfillmentFee?: number;
  fbtStorageFee?: number;
  transactionRate?: number;
  transactionFixed?: number;
  serviceFeeRate?: number;
  serviceFeeFixed?: number;
  shippingSubsidy?: number;
  ozonPlacementFee?: number;
  ebayFinalValueRate?: number;
  ebayPerOrderFee?: number;
  ebayInsertionFee?: number;
  ebayInternationalRate?: number;
  etsyListingFee?: number;
  etsyTransactionRate?: number;
  etsyPaymentRate?: number;
  etsyPaymentFixed?: number;
  etsyOffsiteAdsRate?: number;
}

interface BreakdownItem {
  label: string;
  amount: number;
  variant?: 'cost' | 'fee' | 'total';
}

const OZON_CATEGORIES: Record<string, { name: string; rate: number }> = {
  '12': { name: 'Apparel & Accessories', rate: 0.12 },
  '15': { name: 'Home Decor & Furniture', rate: 0.15 },
  '8': { name: 'Consumer Electronics', rate: 0.08 },
  '10': { name: 'Beauty & Health', rate: 0.1 },
  '5': { name: 'Automotive Parts', rate: 0.05 },
};

const AMAZON_CATEGORIES = [
  { id: 'home', name: 'Home & Kitchen', rate: 0.15 },
  { id: 'beauty', name: 'Beauty & Health', rate: 0.15 },
  { id: 'electronics', name: 'Consumer Electronics', rate: 0.08 },
  { id: 'apparel', name: 'Apparel & Accessories', rate: 0.17 },
  { id: 'jewelry', name: 'Jewelry', rate: 0.2 },
  { id: 'toys', name: 'Toys & Games', rate: 0.15 },
];

const TIKTOK_CATEGORIES = [
  { id: 'general', name: 'General', rate: 0.06 },
  { id: 'collectibles', name: 'Collectibles', rate: 0.05 },
];

const TIKTOK_REGION_PRESETS: Record<
  TikTokRegion,
  { labelKey: string; commission: number; transactionRate: number; transactionFixed: number }
> = {
  us: { labelKey: 'pricingEngine.regionUs', commission: 0.06, transactionRate: 0.029, transactionFixed: 0.3 },
  uk: { labelKey: 'pricingEngine.regionUk', commission: 0.06, transactionRate: 0.029, transactionFixed: 0.3 },
  sea: { labelKey: 'pricingEngine.regionSea', commission: 0.06, transactionRate: 0.0378, transactionFixed: 0 },
};

const WALMART_CATEGORIES = [
  { id: 'home', name: 'Home & Kitchen', rate: 0.15 },
  { id: 'apparel', name: 'Apparel & Accessories', rate: 0.15 },
  { id: 'electronics', name: 'Electronics', rate: 0.08 },
  { id: 'computers', name: 'Personal Computers', rate: 0.06 },
  { id: 'jewelry', name: 'Jewelry & Precious Metals', rate: 0.2 },
  { id: 'auto', name: 'Automotive', rate: 0.12 },
];

const ALIEXPRESS_CATEGORIES = [
  { id: 'general', name: 'General Merchandise', rate: 0.05 },
  { id: 'electronics', name: 'Consumer Electronics', rate: 0.05 },
  { id: 'fashion', name: 'Fashion & Accessories', rate: 0.08 },
  { id: 'beauty', name: 'Beauty & Personal Care', rate: 0.08 },
];

const PLATFORM_META: Record<
  PlatformKey,
  { labelKey: string; shortLabel: string; currency: { code: string; symbol: string }; icon: React.ReactNode }
> = {
  amazon: {
    labelKey: 'pricingEngine.platforms.amazon',
    shortLabel: 'Amazon',
    currency: { code: 'USD', symbol: '$' },
    icon: <Inventory2 fontSize="small" />,
  },
  tiktok: {
    labelKey: 'pricingEngine.platforms.tiktok',
    shortLabel: 'TikTok Shop',
    currency: { code: 'USD', symbol: '$' },
    icon: <Videocam fontSize="small" />,
  },
  ozon: {
    labelKey: 'pricingEngine.platforms.ozon',
    shortLabel: 'Ozon',
    currency: { code: 'RUB', symbol: '₽' },
    icon: <ShoppingCart fontSize="small" />,
  },
  ebay: {
    labelKey: 'pricingEngine.platforms.ebay',
    shortLabel: 'eBay',
    currency: { code: 'USD', symbol: '$' },
    icon: <LocalMall fontSize="small" />,
  },
  walmart: {
    labelKey: 'pricingEngine.platforms.walmart',
    shortLabel: 'Walmart',
    currency: { code: 'USD', symbol: '$' },
    icon: <Storefront fontSize="small" />,
  },
  etsy: {
    labelKey: 'pricingEngine.platforms.etsy',
    shortLabel: 'Etsy',
    currency: { code: 'USD', symbol: '$' },
    icon: <Diamond fontSize="small" />,
  },
  temu: {
    labelKey: 'pricingEngine.platforms.temu',
    shortLabel: 'Temu',
    currency: { code: 'USD', symbol: '$' },
    icon: <LocalOffer fontSize="small" />,
  },
  aliexpress: {
    labelKey: 'pricingEngine.platforms.aliexpress',
    shortLabel: 'AliExpress',
    currency: { code: 'USD', symbol: '$' },
    icon: <Public fontSize="small" />,
  },
  shopee: {
    labelKey: 'pricingEngine.platforms.shopee',
    shortLabel: 'Shopee',
    currency: { code: 'USD', symbol: '$' },
    icon: <ShoppingBag fontSize="small" />,
  },
};

const PLATFORM_THEMES: Record<
  PlatformKey,
  {
    summary: string;
    accent: string;
    badge: string;
    page: string;
    card: string;
    cardBorder: string;
    cardHeader: string;
    cardMuted: string;
    watermark: string;
  }
> = {
  amazon: {
    summary: 'bg-gradient-to-br from-slate-900 to-slate-800',
    accent: 'text-amber-200',
    badge: 'bg-amber-400/20 text-amber-200',
    page: 'bg-amber-50/40',
    card: 'bg-amber-50/80',
    cardBorder: 'border border-amber-100/80',
    cardHeader: 'bg-amber-100/40',
    cardMuted: 'bg-amber-100/30',
    watermark: 'opacity-70',
  },
  tiktok: {
    summary: 'bg-gradient-to-br from-slate-900 via-slate-800 to-pink-600/70',
    accent: 'text-pink-200',
    badge: 'bg-pink-400/20 text-pink-200',
    page: 'bg-pink-50/40',
    card: 'bg-pink-50/80',
    cardBorder: 'border border-pink-100/80',
    cardHeader: 'bg-pink-100/40',
    cardMuted: 'bg-pink-100/25',
    watermark: 'opacity-70',
  },
  ozon: {
    summary: 'bg-primary',
    accent: 'text-blue-100',
    badge: 'bg-white/15 text-white',
    page: 'bg-blue-50/40',
    card: 'bg-blue-50/80',
    cardBorder: 'border border-blue-100/80',
    cardHeader: 'bg-blue-100/40',
    cardMuted: 'bg-blue-100/25',
    watermark: 'opacity-70',
  },
  ebay: {
    summary: 'bg-gradient-to-br from-[#0064D2] to-[#1e40af]',
    accent: 'text-blue-100',
    badge: 'bg-white/15 text-white',
    page: 'bg-blue-50/40',
    card: 'bg-blue-50/80',
    cardBorder: 'border border-blue-100/80',
    cardHeader: 'bg-blue-100/40',
    cardMuted: 'bg-blue-100/25',
    watermark: 'opacity-70',
  },
  walmart: {
    summary: 'bg-gradient-to-br from-[#004F9F] to-[#0071CE]',
    accent: 'text-blue-100',
    badge: 'bg-white/15 text-white',
    page: 'bg-blue-50/40',
    card: 'bg-blue-50/80',
    cardBorder: 'border border-blue-100/80',
    cardHeader: 'bg-blue-100/40',
    cardMuted: 'bg-blue-100/25',
    watermark: 'opacity-70',
  },
  etsy: {
    summary: 'bg-gradient-to-br from-[#F1641E] to-[#c2410c]',
    accent: 'text-orange-100',
    badge: 'bg-white/15 text-white',
    page: 'bg-orange-50/40',
    card: 'bg-orange-50/80',
    cardBorder: 'border border-orange-100/80',
    cardHeader: 'bg-orange-100/40',
    cardMuted: 'bg-orange-100/30',
    watermark: 'opacity-70',
  },
  temu: {
    summary: 'bg-gradient-to-br from-[#f59e0b] via-[#fb923c] to-[#f97316]',
    accent: 'text-amber-100',
    badge: 'bg-white/20 text-white',
    page: 'bg-amber-50/40',
    card: 'bg-amber-50/80',
    cardBorder: 'border border-amber-100/80',
    cardHeader: 'bg-amber-100/40',
    cardMuted: 'bg-amber-100/30',
    watermark: 'opacity-70',
  },
  aliexpress: {
    summary: 'bg-gradient-to-br from-[#E62E04] to-[#b91c1c]',
    accent: 'text-red-100',
    badge: 'bg-white/15 text-white',
    page: 'bg-red-50/40',
    card: 'bg-red-50/80',
    cardBorder: 'border border-red-100/80',
    cardHeader: 'bg-red-100/40',
    cardMuted: 'bg-red-100/25',
    watermark: 'opacity-70',
  },
  shopee: {
    summary: 'bg-gradient-to-br from-[#ee4d2d] to-[#f97316]',
    accent: 'text-orange-100',
    badge: 'bg-white/15 text-white',
    page: 'bg-orange-50/40',
    card: 'bg-orange-50/80',
    cardBorder: 'border border-orange-100/80',
    cardHeader: 'bg-orange-100/40',
    cardMuted: 'bg-orange-100/30',
    watermark: 'opacity-70',
  },
};

const PLATFORM_BRAND_LOGOS: Record<
  PlatformKey,
  { src: string; alt: string; layout?: 'wide' | 'square' }
> = {
  amazon: {
    src: 'https://www.vectorlogo.zone/logos/amazon/amazon-icon.svg',
    alt: 'Amazon',
    layout: 'square',
  },
  tiktok: {
    src: 'https://www.vectorlogo.zone/logos/tiktok/tiktok-icon.svg',
    alt: 'TikTok',
    layout: 'square',
  },
  ozon: {
    src: 'about:blank',
    alt: 'Ozon',
    layout: 'square',
  },
  ebay: {
    src: 'https://www.vectorlogo.zone/logos/ebay/ebay-icon.svg',
    alt: 'eBay',
    layout: 'wide',
  },
  walmart: {
    src: 'https://www.vectorlogo.zone/logos/walmart/walmart-icon.svg',
    alt: 'Walmart',
    layout: 'square',
  },
  etsy: {
    src: 'https://www.vectorlogo.zone/logos/etsy/etsy-icon.svg',
    alt: 'Etsy',
    layout: 'wide',
  },
  temu: {
    src: 'https://commons.wikimedia.org/wiki/Special:FilePath/Temu_logo_icon.svg',
    alt: 'Temu',
    layout: 'square',
  },
  aliexpress: {
    src: 'https://www.vectorlogo.zone/logos/aliexpress/aliexpress-icon.svg',
    alt: 'AliExpress',
    layout: 'wide',
  },
  shopee: {
    src: 'https://commons.wikimedia.org/wiki/Special:FilePath/Shopee_logo.svg',
    alt: 'Shopee',
    layout: 'wide',
  },
};

const PLATFORM_ORDER: PlatformKey[] = ['amazon', 'tiktok', 'ozon', 'temu', 'aliexpress', 'shopee', 'ebay', 'walmart', 'etsy'];

const PLATFORM_SHARE_COLORS: Record<PlatformKey, { from: string; to: string; accent: string }> = {
  amazon: { from: '#0f172a', to: '#1e293b', accent: '#fbbf24' },
  tiktok: { from: '#0f172a', to: '#db2777', accent: '#f472b6' },
  ozon: { from: '#2563eb', to: '#1e40af', accent: '#bfdbfe' },
  temu: { from: '#f59e0b', to: '#f97316', accent: '#fef3c7' },
  aliexpress: { from: '#E62E04', to: '#b91c1c', accent: '#fecaca' },
  shopee: { from: '#ee4d2d', to: '#f97316', accent: '#ffedd5' },
  ebay: { from: '#0064D2', to: '#1e40af', accent: '#bfdbfe' },
  walmart: { from: '#004F9F', to: '#0071CE', accent: '#bfdbfe' },
  etsy: { from: '#F1641E', to: '#c2410c', accent: '#fed7aa' },
};

const createDefaultInputs = (): Record<PlatformKey, PlatformInput> => ({
  amazon: {
    purchaseCost: 120,
    shippingCost: 18,
    packagingCost: 3,
    otherCost: 0,
    shippingCharged: 0,
    weight: 1,
    dims: { l: 30, w: 20, h: 10 },
    exchangeRate: 0.14,
    targetMargin: 35,
    targetProfit: 80,
    categoryId: 'home',
    referralRate: 0.15,
    referralMinFee: 0.3,
    closingFee: 0,
    sellingPlan: 'individual',
    sellingPlanFee: 0.99,
    fulfillmentMode: 'fba',
    fulfillmentFee: 0,
    storageFee: 0,
    inboundShippingFee: 0,
    fbmHandlingFee: 0,
  },
  tiktok: {
    purchaseCost: 120,
    shippingCost: 18,
    packagingCost: 3,
    otherCost: 0,
    shippingCharged: 0,
    weight: 1,
    dims: { l: 30, w: 20, h: 10 },
    exchangeRate: 0.14,
    targetMargin: 35,
    targetProfit: 80,
    categoryId: 'general',
    referralRate: 0.06,
    transactionRate: 0.029,
    transactionFixed: 0.3,
    platformDiscount: 0,
    tax: 0,
    sellerDiscount: 0,
    affiliateRate: 0,
    affiliatePartnerRate: 0,
    adsRate: 0,
    tiktokRegion: 'us',
    tiktokShippingType: 'platform',
    fbtFulfillmentFee: 3.58,
    fbtStorageFee: 0,
  },
  ozon: {
    purchaseCost: 112.5,
    shippingCost: 12,
    packagingCost: 2,
    otherCost: 0,
    shippingCharged: 0,
    weight: 2.45,
    dims: { l: 35, w: 25, h: 15 },
    exchangeRate: 12.94,
    targetMargin: 35,
    targetProfit: 300,
    categoryId: '15',
    ozonPlacementFee: 0,
  },
  temu: {
    purchaseCost: 120,
    shippingCost: 18,
    packagingCost: 3,
    otherCost: 0,
    shippingCharged: 0,
    weight: 1,
    dims: { l: 30, w: 20, h: 10 },
    exchangeRate: 0.14,
    targetMargin: 35,
    targetProfit: 80,
    referralRate: 0,
    serviceFeeRate: 0.0005,
    serviceFeeFixed: 0,
    fulfillmentFee: 0,
    adsRate: 0,
    platformDiscount: 0,
    tax: 0,
  },
  aliexpress: {
    purchaseCost: 120,
    shippingCost: 18,
    packagingCost: 3,
    otherCost: 0,
    shippingCharged: 0,
    weight: 1,
    dims: { l: 30, w: 20, h: 10 },
    exchangeRate: 0.14,
    targetMargin: 35,
    targetProfit: 80,
    categoryId: 'general',
    referralRate: 0.05,
    transactionRate: 0.025,
    transactionFixed: 0,
    serviceFeeFixed: 0,
    adsRate: 0,
    platformDiscount: 0,
    tax: 0,
  },
  shopee: {
    purchaseCost: 120,
    shippingCost: 18,
    packagingCost: 3,
    otherCost: 0,
    shippingCharged: 0,
    weight: 1,
    dims: { l: 30, w: 20, h: 10 },
    exchangeRate: 0.14,
    targetMargin: 35,
    targetProfit: 80,
    referralRate: 0.02,
    transactionRate: 0.02,
    transactionFixed: 0,
    serviceFeeRate: 0,
    serviceFeeFixed: 0,
    shippingSubsidy: 0,
    adsRate: 0,
    tax: 0,
  },
  ebay: {
    purchaseCost: 120,
    shippingCost: 18,
    packagingCost: 3,
    otherCost: 0,
    shippingCharged: 0,
    weight: 1,
    dims: { l: 30, w: 20, h: 10 },
    exchangeRate: 0.14,
    targetMargin: 35,
    targetProfit: 80,
    ebayFinalValueRate: 0.136,
    ebayPerOrderFee: 0.4,
    ebayInsertionFee: 0,
    ebayInternationalRate: 0,
    tax: 0,
  },
  walmart: {
    purchaseCost: 120,
    shippingCost: 18,
    packagingCost: 3,
    otherCost: 0,
    shippingCharged: 0,
    weight: 1,
    dims: { l: 30, w: 20, h: 10 },
    exchangeRate: 0.14,
    targetMargin: 35,
    targetProfit: 80,
    categoryId: 'home',
    referralRate: 0.15,
  },
  etsy: {
    purchaseCost: 120,
    shippingCost: 18,
    packagingCost: 3,
    otherCost: 0,
    shippingCharged: 0,
    weight: 1,
    dims: { l: 30, w: 20, h: 10 },
    exchangeRate: 0.14,
    targetMargin: 35,
    targetProfit: 80,
    etsyListingFee: 0.2,
    etsyTransactionRate: 0.065,
    etsyPaymentRate: 0.03,
    etsyPaymentFixed: 0.25,
    etsyOffsiteAdsRate: 0,
    tax: 0,
  },
});

interface NumberFieldProps {
  label: string;
  value: number;
  onChange: (value: number) => void;
  prefix?: string;
  suffix?: string;
  disabled?: boolean;
  min?: number;
  step?: number;
  helper?: string;
}

const NumberField: React.FC<NumberFieldProps> = ({
  label,
  value,
  onChange,
  prefix,
  suffix,
  disabled,
  min,
  step,
  helper,
}) => {
  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-2">{label}</label>
      <div className="relative">
        {prefix && (
          <span className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-xs">
            {prefix}
          </span>
        )}
        {suffix && (
          <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-medium text-xs">
            {suffix}
          </span>
        )}
        <input
          className={`w-full px-4 py-2.5 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all ${
            prefix ? 'pl-8' : ''
          } ${suffix ? 'pr-8' : ''} ${disabled ? 'bg-slate-50 text-slate-400 cursor-not-allowed' : ''}`}
          placeholder="0"
          type="number"
          min={min}
          step={step}
          value={Number.isFinite(value) ? value : 0}
          onChange={(e) => onChange(Number(e.target.value))}
          disabled={disabled}
        />
      </div>
      {helper && <p className="text-[11px] text-slate-500 mt-1">{helper}</p>}
    </div>
  );
};

const clampNumber = (value: number, min: number, max: number) => Math.min(max, Math.max(min, value));

const safeNumber = (value?: number | null) => {
  if (typeof value !== 'number' || !Number.isFinite(value)) {
    return 0;
  }
  return value;
};

const calcMaxMargin = (platform: PlatformKey, inputs: PlatformInput) => {
  let variableRate = 0;
  if (platform === 'amazon') {
    variableRate = safeNumber(inputs.referralRate);
  }
  if (platform === 'tiktok') {
    variableRate =
      safeNumber(inputs.referralRate) +
      safeNumber(inputs.transactionRate) +
      safeNumber(inputs.affiliateRate) +
      safeNumber(inputs.affiliatePartnerRate) +
      safeNumber(inputs.adsRate);
  }
  if (platform === 'ozon') {
    const categoryRate = OZON_CATEGORIES[inputs.categoryId ?? '15']?.rate ?? 0.15;
    variableRate = categoryRate;
  }
  if (platform === 'ebay') {
    variableRate = safeNumber(inputs.ebayFinalValueRate) + safeNumber(inputs.ebayInternationalRate);
  }
  if (platform === 'walmart') {
    variableRate = safeNumber(inputs.referralRate);
  }
  if (platform === 'etsy') {
    variableRate =
      safeNumber(inputs.etsyTransactionRate) + safeNumber(inputs.etsyPaymentRate) + safeNumber(inputs.etsyOffsiteAdsRate);
  }
  if (platform === 'temu') {
    variableRate = safeNumber(inputs.referralRate) + safeNumber(inputs.serviceFeeRate) + safeNumber(inputs.adsRate);
  }
  if (platform === 'aliexpress') {
    variableRate = safeNumber(inputs.referralRate) + safeNumber(inputs.transactionRate) + safeNumber(inputs.adsRate);
  }
  if (platform === 'shopee') {
    variableRate =
      safeNumber(inputs.referralRate) + safeNumber(inputs.transactionRate) + safeNumber(inputs.serviceFeeRate) + safeNumber(inputs.adsRate);
  }
  const maxMargin = 1 - Math.max(0, variableRate);
  return clampNumber(maxMargin, 0, 0.95);
};

const fmt = (num: number) =>
  safeNumber(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const FIELD_ROW_CLASS = 'grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-4';
const FIELD_ROW_TIGHT = 'grid grid-cols-[repeat(auto-fit,minmax(120px,1fr))] gap-3';

export const PricingEngineScreen: React.FC = () => {
  const { t } = useLanguage();

  const [activePlatform, setActivePlatform] = useState<PlatformKey>('amazon');
  const [strategyType, setStrategyType] = useState<StrategyType>('margin');
  const [compactMode, setCompactMode] = useState(true);
  const [autoRate, setAutoRate] = useState(true);
  const [rateStatus, setRateStatus] = useState<'idle' | 'loading' | 'error'>('idle');
  const [rateUpdatedAt, setRateUpdatedAt] = useState<string | null>(null);
  const [rateRefreshTick, setRateRefreshTick] = useState(0);
  const [toast, setToast] = useState<{ show: boolean; title: string; msg: string } | null>(null);
  const [platformInputs, setPlatformInputs] = useState<Record<PlatformKey, PlatformInput>>(() => createDefaultInputs());

  const activeInputs = platformInputs[activePlatform];
  const activeCurrency = PLATFORM_META[activePlatform].currency;
  const activeTheme = PLATFORM_THEMES[activePlatform];
  const maxMarginRate = useMemo(() => calcMaxMargin(activePlatform, activeInputs), [activePlatform, activeInputs]);
  const maxMarginPercent = useMemo(() => Math.round(maxMarginRate * 1000) / 10, [maxMarginRate]);

  const updatePlatformInput = <K extends keyof PlatformInput>(key: K, value: PlatformInput[K]) => {
    setPlatformInputs((prev) => ({
      ...prev,
      [activePlatform]: {
        ...prev[activePlatform],
        [key]: value,
      },
    }));
  };

  const updateSpecificPlatformInput = <K extends keyof PlatformInput>(
    platform: PlatformKey,
    key: K,
    value: PlatformInput[K],
  ) => {
    setPlatformInputs((prev) => ({
      ...prev,
      [platform]: {
        ...prev[platform],
        [key]: value,
      },
    }));
  };

  const updateDimensions = (patch: Partial<Dimensions>) => {
    updatePlatformInput('dims', {
      ...activeInputs.dims,
      ...patch,
    });
  };

  useEffect(() => {
    if (activeInputs.targetMargin > maxMarginPercent) {
      updatePlatformInput('targetMargin', maxMarginPercent);
    }
  }, [activePlatform, maxMarginPercent, activeInputs.targetMargin]);

  useEffect(() => {
    if (!autoRate) {
      return;
    }
    const platform = activePlatform;
    const currencyCode = PLATFORM_META[platform].currency.code;
    if (currencyCode === 'CNY') {
      updateSpecificPlatformInput(platform, 'exchangeRate', 1);
      setRateUpdatedAt(null);
      return;
    }
    let mounted = true;
    const fetchRate = async () => {
      setRateStatus('loading');
      try {
        const response = await fetch(`https://api.frankfurter.dev/v1/latest?base=CNY&symbols=${currencyCode}`);
        const data = await response.json();
        const rate = data?.rates?.[currencyCode];
        if (!mounted) {
          return;
        }
        if (typeof rate === 'number') {
          updateSpecificPlatformInput(platform, 'exchangeRate', rate);
          setRateUpdatedAt(data?.date ?? null);
          setRateStatus('idle');
          return;
        }
        throw new Error('invalid rate');
      } catch (error) {
        if (!mounted) {
          return;
        }
        setRateStatus('error');
        setToast({ show: true, title: t('pricingEngine.rateError'), msg: t('pricingEngine.rateErrorDesc') });
        setTimeout(() => setToast(null), 4000);
      }
    };
    fetchRate();
    return () => {
      mounted = false;
    };
  }, [activePlatform, autoRate, rateRefreshTick, t]);

  const handleAmazonCategoryChange = (categoryId: string) => {
    const category = AMAZON_CATEGORIES.find((item) => item.id === categoryId);
    updateSpecificPlatformInput('amazon', 'categoryId', categoryId);
    if (category) {
      updateSpecificPlatformInput('amazon', 'referralRate', category.rate);
    }
  };

  const handleTikTokCategoryChange = (categoryId: string) => {
    const category = TIKTOK_CATEGORIES.find((item) => item.id === categoryId);
    updateSpecificPlatformInput('tiktok', 'categoryId', categoryId);
    if (category) {
      updateSpecificPlatformInput('tiktok', 'referralRate', category.rate);
    }
  };

  const handleTikTokRegionChange = (region: TikTokRegion) => {
    const preset = TIKTOK_REGION_PRESETS[region];
    setPlatformInputs((prev) => ({
      ...prev,
      tiktok: {
        ...prev.tiktok,
        tiktokRegion: region,
        referralRate: preset.commission,
        transactionRate: preset.transactionRate,
        transactionFixed: preset.transactionFixed,
      },
    }));
  };

  const handleWalmartCategoryChange = (categoryId: string) => {
    const category = WALMART_CATEGORIES.find((item) => item.id === categoryId);
    updateSpecificPlatformInput('walmart', 'categoryId', categoryId);
    if (category) {
      updateSpecificPlatformInput('walmart', 'referralRate', category.rate);
    }
  };

  const handleAliExpressCategoryChange = (categoryId: string) => {
    const category = ALIEXPRESS_CATEGORIES.find((item) => item.id === categoryId);
    updateSpecificPlatformInput('aliexpress', 'categoryId', categoryId);
    if (category) {
      updateSpecificPlatformInput('aliexpress', 'referralRate', category.rate);
    }
  };

  const handleRefreshRate = () => {
    setAutoRate(true);
    setRateRefreshTick((prev) => prev + 1);
  };

  const calculations = useMemo(() => {
    const exchangeRate = safeNumber(activeInputs.exchangeRate);
    const shippingCharged = safeNumber(activeInputs.shippingCharged);
    const platformDiscount = safeNumber(activeInputs.platformDiscount);
    const tax = safeNumber(activeInputs.tax);

    const baseCostCny =
      safeNumber(activeInputs.purchaseCost) +
      safeNumber(activeInputs.shippingCost) +
      safeNumber(activeInputs.packagingCost) +
      safeNumber(activeInputs.otherCost);
    const baseCost = baseCostCny * exchangeRate;

    const baseCostItems = ([
      {
        label: t('pricingEngine.purchaseCostLabel'),
        amount: safeNumber(activeInputs.purchaseCost) * exchangeRate,
        variant: 'cost',
      },
      {
        label: t('pricingEngine.shippingCostLabel'),
        amount: safeNumber(activeInputs.shippingCost) * exchangeRate,
        variant: 'cost',
      },
      {
        label: t('pricingEngine.packagingCostLabel'),
        amount: safeNumber(activeInputs.packagingCost) * exchangeRate,
        variant: 'cost',
      },
      {
        label: t('pricingEngine.otherCostLabel'),
        amount: safeNumber(activeInputs.otherCost) * exchangeRate,
        variant: 'cost',
      },
    ] as BreakdownItem[]).filter((item) => item.amount > 0);

    const computeMetrics = (price: number) => {
      const revenue = price + shippingCharged + platformDiscount;
      const feeItems: BreakdownItem[] = [];

      if (activePlatform === 'amazon') {
        const referralRate = safeNumber(activeInputs.referralRate);
        const referralMin = safeNumber(activeInputs.referralMinFee);
        const referralBase = price + shippingCharged;
        let referralFee = referralBase * referralRate;
        if (referralMin > 0 && referralFee < referralMin) {
          referralFee = referralMin;
        }
        const sellingPlanFee = activeInputs.sellingPlan === 'individual' ? safeNumber(activeInputs.sellingPlanFee) : 0;
        const closingFee = safeNumber(activeInputs.closingFee);
        const fulfillmentFee =
          activeInputs.fulfillmentMode === 'fba'
            ? safeNumber(activeInputs.fulfillmentFee)
            : safeNumber(activeInputs.fbmHandlingFee);
        const storageFee = activeInputs.fulfillmentMode === 'fba' ? safeNumber(activeInputs.storageFee) : 0;
        const inboundShippingFee = activeInputs.fulfillmentMode === 'fba' ? safeNumber(activeInputs.inboundShippingFee) : 0;

        feeItems.push(
          { label: `${t('pricingEngine.referralFeeLabel')} (${(referralRate * 100).toFixed(2)}%)`, amount: referralFee, variant: 'fee' },
          { label: t('pricingEngine.sellingPlanFee'), amount: sellingPlanFee, variant: 'fee' },
          { label: t('pricingEngine.closingFeeLabel'), amount: closingFee, variant: 'fee' },
          { label: t('pricingEngine.fulfillmentFee'), amount: fulfillmentFee, variant: 'fee' },
          { label: t('pricingEngine.storageFee'), amount: storageFee, variant: 'fee' },
          { label: t('pricingEngine.inboundShippingFee'), amount: inboundShippingFee, variant: 'fee' },
        );
      }

      if (activePlatform === 'tiktok') {
        const referralRate = safeNumber(activeInputs.referralRate);
        const transactionRate = safeNumber(activeInputs.transactionRate);
        const transactionFixed = safeNumber(activeInputs.transactionFixed);
        const sellerDiscount = safeNumber(activeInputs.sellerDiscount);
        const commissionBase = Math.max(0, price - sellerDiscount);
        const commissionFee = commissionBase * referralRate;
        const transactionBase = commissionBase + shippingCharged;
        const transactionRateFee = transactionBase * transactionRate;
        const affiliateBase = Math.max(0, price - sellerDiscount);
        const affiliateFee = affiliateBase * safeNumber(activeInputs.affiliateRate);
        const partnerFee = affiliateBase * safeNumber(activeInputs.affiliatePartnerRate);
        const adsFee = price * safeNumber(activeInputs.adsRate);
        const fbtFulfillmentFee = activeInputs.tiktokShippingType === 'fbt' ? safeNumber(activeInputs.fbtFulfillmentFee) : 0;
        const fbtStorageFee = activeInputs.tiktokShippingType === 'fbt' ? safeNumber(activeInputs.fbtStorageFee) : 0;

        feeItems.push(
          { label: `${t('pricingEngine.referralFeeLabel')} (${(referralRate * 100).toFixed(2)}%)`, amount: commissionFee, variant: 'fee' },
          { label: `${t('pricingEngine.transactionFeeRate')} (${(transactionRate * 100).toFixed(2)}%)`, amount: transactionRateFee, variant: 'fee' },
          { label: t('pricingEngine.transactionFeeFixed'), amount: transactionFixed, variant: 'fee' },
          { label: t('pricingEngine.affiliateRate'), amount: affiliateFee, variant: 'fee' },
          { label: t('pricingEngine.affiliatePartnerRate'), amount: partnerFee, variant: 'fee' },
          { label: t('pricingEngine.adsRate'), amount: adsFee, variant: 'fee' },
          { label: t('pricingEngine.fbtFulfillmentFee'), amount: fbtFulfillmentFee, variant: 'fee' },
          { label: t('pricingEngine.fbtStorageFee'), amount: fbtStorageFee, variant: 'fee' },
          { label: t('pricingEngine.tax'), amount: tax, variant: 'fee' },
        );
      }

      if (activePlatform === 'ozon') {
        const categoryRate = OZON_CATEGORIES[activeInputs.categoryId ?? '15']?.rate ?? 0.15;
        const commissionFee = price * categoryRate;
        const lastMileFee = 150 + safeNumber(activeInputs.weight) * 20;
        const handlingFee = 50 + safeNumber(activeInputs.weight) * 10;
        const placementFee = safeNumber(activeInputs.ozonPlacementFee);

        feeItems.push(
          { label: `${t('pricingEngine.referralFeeLabel')} (${(categoryRate * 100).toFixed(2)}%)`, amount: commissionFee, variant: 'fee' },
          { label: t('pricingEngine.lastMile'), amount: lastMileFee, variant: 'fee' },
          { label: t('pricingEngine.handling'), amount: handlingFee, variant: 'fee' },
          { label: t('pricingEngine.ozonPlacementFee'), amount: placementFee, variant: 'fee' },
        );
      }

      if (activePlatform === 'temu') {
        const commissionRate = safeNumber(activeInputs.referralRate);
        const serviceRate = safeNumber(activeInputs.serviceFeeRate);
        const serviceFixed = safeNumber(activeInputs.serviceFeeFixed);
        const fulfillmentFee = safeNumber(activeInputs.fulfillmentFee);
        const adsFee = price * safeNumber(activeInputs.adsRate);
        const commissionFee = price * commissionRate;
        const serviceRateFee = price * serviceRate;

        feeItems.push(
          { label: `${t('pricingEngine.referralFeeLabel')} (${(commissionRate * 100).toFixed(2)}%)`, amount: commissionFee, variant: 'fee' },
          { label: `${t('pricingEngine.serviceFeeRate')} (${(serviceRate * 100).toFixed(2)}%)`, amount: serviceRateFee, variant: 'fee' },
          { label: t('pricingEngine.serviceFeeFixed'), amount: serviceFixed, variant: 'fee' },
          { label: t('pricingEngine.fulfillmentFee'), amount: fulfillmentFee, variant: 'fee' },
          { label: t('pricingEngine.adsRate'), amount: adsFee, variant: 'fee' },
          { label: t('pricingEngine.tax'), amount: tax, variant: 'fee' },
        );
      }

      if (activePlatform === 'aliexpress') {
        const commissionRate = safeNumber(activeInputs.referralRate);
        const transactionRate = safeNumber(activeInputs.transactionRate);
        const transactionFixed = safeNumber(activeInputs.transactionFixed);
        const serviceFixed = safeNumber(activeInputs.serviceFeeFixed);
        const commissionFee = price * commissionRate;
        const transactionRateFee = (price + shippingCharged) * transactionRate;
        const adsFee = price * safeNumber(activeInputs.adsRate);

        feeItems.push(
          { label: `${t('pricingEngine.referralFeeLabel')} (${(commissionRate * 100).toFixed(2)}%)`, amount: commissionFee, variant: 'fee' },
          { label: `${t('pricingEngine.transactionFeeRate')} (${(transactionRate * 100).toFixed(2)}%)`, amount: transactionRateFee, variant: 'fee' },
          { label: t('pricingEngine.transactionFeeFixed'), amount: transactionFixed, variant: 'fee' },
          { label: t('pricingEngine.orderServiceFee'), amount: serviceFixed, variant: 'fee' },
          { label: t('pricingEngine.adsRate'), amount: adsFee, variant: 'fee' },
          { label: t('pricingEngine.tax'), amount: tax, variant: 'fee' },
        );
      }

      if (activePlatform === 'shopee') {
        const commissionRate = safeNumber(activeInputs.referralRate);
        const transactionRate = safeNumber(activeInputs.transactionRate);
        const transactionFixed = safeNumber(activeInputs.transactionFixed);
        const serviceRate = safeNumber(activeInputs.serviceFeeRate);
        const serviceFixed = safeNumber(activeInputs.serviceFeeFixed);
        const shippingSubsidy = safeNumber(activeInputs.shippingSubsidy);
        const commissionFee = price * commissionRate;
        const transactionRateFee = (price + shippingCharged) * transactionRate;
        const serviceRateFee = price * serviceRate;
        const adsFee = price * safeNumber(activeInputs.adsRate);

        feeItems.push(
          { label: `${t('pricingEngine.referralFeeLabel')} (${(commissionRate * 100).toFixed(2)}%)`, amount: commissionFee, variant: 'fee' },
          { label: `${t('pricingEngine.transactionFeeRate')} (${(transactionRate * 100).toFixed(2)}%)`, amount: transactionRateFee, variant: 'fee' },
          { label: t('pricingEngine.transactionFeeFixed'), amount: transactionFixed, variant: 'fee' },
          { label: `${t('pricingEngine.serviceFeeRate')} (${(serviceRate * 100).toFixed(2)}%)`, amount: serviceRateFee, variant: 'fee' },
          { label: t('pricingEngine.platformSupportFee'), amount: serviceFixed, variant: 'fee' },
          { label: t('pricingEngine.shippingSubsidy'), amount: shippingSubsidy, variant: 'fee' },
          { label: t('pricingEngine.adsRate'), amount: adsFee, variant: 'fee' },
          { label: t('pricingEngine.tax'), amount: tax, variant: 'fee' },
        );
      }

      if (activePlatform === 'ebay') {
        const finalValueRate = safeNumber(activeInputs.ebayFinalValueRate);
        const perOrderFee = safeNumber(activeInputs.ebayPerOrderFee);
        const insertionFee = safeNumber(activeInputs.ebayInsertionFee);
        const internationalRate = safeNumber(activeInputs.ebayInternationalRate);
        const finalValueFee = (price + shippingCharged + tax) * finalValueRate + perOrderFee;
        const internationalFee = (price + shippingCharged + tax) * internationalRate;

        feeItems.push(
          { label: `${t('pricingEngine.ebayFinalValueRate')} (${(finalValueRate * 100).toFixed(2)}%)`, amount: finalValueFee, variant: 'fee' },
          { label: t('pricingEngine.ebayInsertionFee'), amount: insertionFee, variant: 'fee' },
          { label: t('pricingEngine.ebayInternationalRate'), amount: internationalFee, variant: 'fee' },
          { label: t('pricingEngine.tax'), amount: tax, variant: 'fee' },
        );
      }

      if (activePlatform === 'walmart') {
        const referralRate = safeNumber(activeInputs.referralRate);
        const referralFee = (price + shippingCharged) * referralRate;
        feeItems.push({ label: `${t('pricingEngine.referralFeeLabel')} (${(referralRate * 100).toFixed(2)}%)`, amount: referralFee, variant: 'fee' });
      }

      if (activePlatform === 'etsy') {
        const listingFee = safeNumber(activeInputs.etsyListingFee);
        const transactionRate = safeNumber(activeInputs.etsyTransactionRate);
        const paymentRate = safeNumber(activeInputs.etsyPaymentRate);
        const paymentFixed = safeNumber(activeInputs.etsyPaymentFixed);
        const offsiteAdsRate = safeNumber(activeInputs.etsyOffsiteAdsRate);
        const transactionFee = (price + shippingCharged) * transactionRate;
        const paymentFee = (price + shippingCharged + tax) * paymentRate + paymentFixed;
        const offsiteAdsFee = price * offsiteAdsRate;

        feeItems.push(
          { label: t('pricingEngine.etsyListingFee'), amount: listingFee, variant: 'fee' },
          { label: `${t('pricingEngine.etsyTransactionFee')} (${(transactionRate * 100).toFixed(2)}%)`, amount: transactionFee, variant: 'fee' },
          { label: `${t('pricingEngine.etsyPaymentRate')} (${(paymentRate * 100).toFixed(2)}%)`, amount: paymentFee, variant: 'fee' },
          { label: t('pricingEngine.etsyOffsiteAdsRate'), amount: offsiteAdsFee, variant: 'fee' },
          { label: t('pricingEngine.tax'), amount: tax, variant: 'fee' },
        );
      }

      const platformFeeTotal = feeItems.reduce((sum, item) => sum + item.amount, 0);
      const totalCost = baseCost + platformFeeTotal;
      const profit = revenue - totalCost;
      const margin = revenue > 0 ? profit / revenue : 0;
      const roi = baseCost > 0 ? profit / baseCost : 0;

      return {
        revenue,
        profit,
        margin,
        roi,
        feeItems: feeItems.filter((item) => item.amount > 0),
        totalCost,
        baseCost,
        platformFeeTotal,
      };
    };

    const solvePrice = (target: number, mode: 'profit' | 'margin') => {
      let low = 0;
      let high = Math.max(baseCost * 4, 10);
      for (let i = 0; i < 20; i += 1) {
        const metric = mode === 'profit' ? computeMetrics(high).profit : computeMetrics(high).margin;
        if (metric >= target) {
          break;
        }
        high *= 2;
        if (high > 1_000_000) {
          break;
        }
      }

      for (let i = 0; i < 40; i += 1) {
        const mid = (low + high) / 2;
        const metric = mode === 'profit' ? computeMetrics(mid).profit : computeMetrics(mid).margin;
        if (metric >= target) {
          high = mid;
        } else {
          low = mid;
        }
      }

      return high;
    };

    const targetMarginRaw = clampNumber(safeNumber(activeInputs.targetMargin), 0, 95) / 100;
    const targetMargin = Math.min(targetMarginRaw, maxMarginRate);
    const targetProfit = safeNumber(activeInputs.targetProfit) * exchangeRate;
    const suggestedPrice = strategyType === 'profit' ? solvePrice(targetProfit, 'profit') : solvePrice(targetMargin, 'margin');
    const breakEvenPrice = solvePrice(0, 'profit');
    const metrics = computeMetrics(suggestedPrice);

    const totalCostItems: BreakdownItem[] = [
      ...baseCostItems,
      ...metrics.feeItems,
      { label: t('pricingEngine.totalCost'), amount: metrics.totalCost, variant: 'total' },
    ];

    return {
      suggestedPrice,
      breakEvenPrice,
      profit: metrics.profit,
      profitCny: exchangeRate > 0 ? metrics.profit / exchangeRate : 0,
      margin: metrics.margin,
      roi: metrics.roi,
      baseCostTotal: metrics.baseCost,
      platformFeeTotal: metrics.platformFeeTotal,
      totalCost: metrics.totalCost,
      feeItems: metrics.feeItems,
      baseCostItems,
      totalCostItems,
      exchangeRate,
      revenue: metrics.revenue,
    };
  }, [activeInputs, activePlatform, maxMarginRate, strategyType, t]);

  const renderPlatformSelector = () => (
    <div className={`rounded-xl p-5 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between mb-3">
        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.destPlatform')}</span>
        <span className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.inputMode')}</span>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-[1.4fr_1fr] gap-3">
        <div className="relative">
          <select
            value={activePlatform}
            onChange={(e) => setActivePlatform(e.target.value as PlatformKey)}
            className="w-full px-4 py-2.5 pr-10 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer text-slate-900 font-medium"
          >
            {PLATFORM_ORDER.map((platformKey) => {
              const platform = PLATFORM_META[platformKey];
              return (
                <option key={platformKey} value={platformKey}>
                  {t(platform.labelKey)}
                </option>
              );
            })}
          </select>
          <ExpandMore className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
        <div className="flex p-1 bg-slate-100 rounded-lg">
          <button
            onClick={() => setCompactMode(true)}
            className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
              compactMode ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('pricingEngine.compactMode')}
          </button>
          <button
            onClick={() => setCompactMode(false)}
            className={`flex-1 py-2 text-xs font-bold uppercase tracking-wider rounded-md transition-all ${
              !compactMode ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
            }`}
          >
            {t('pricingEngine.fullMode')}
          </button>
        </div>
      </div>
    </div>
  );

  const renderBaseCostCard = () => (
    <div className={`rounded-xl p-6 space-y-5 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.basicCosts')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.costCurrency').replace('{currency}', activeCurrency.code)}</span>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.purchaseCostLabel')}
          value={safeNumber(activeInputs.purchaseCost)}
          prefix="¥"
          onChange={(value) => updatePlatformInput('purchaseCost', value)}
        />
        <NumberField
          label={t('pricingEngine.shippingCostLabel')}
          value={safeNumber(activeInputs.shippingCost)}
          prefix="¥"
          onChange={(value) => updatePlatformInput('shippingCost', value)}
        />
        <NumberField
          label={t('pricingEngine.shippingCharged')}
          value={safeNumber(activeInputs.shippingCharged)}
          prefix={activeCurrency.symbol}
          onChange={(value) => updatePlatformInput('shippingCharged', value)}
        />
        {!compactMode && (
          <NumberField
            label={t('pricingEngine.packagingCostLabel')}
            value={safeNumber(activeInputs.packagingCost)}
            prefix="¥"
            onChange={(value) => updatePlatformInput('packagingCost', value)}
          />
        )}
        {!compactMode && (
          <NumberField
            label={t('pricingEngine.otherCostLabel')}
            value={safeNumber(activeInputs.otherCost)}
            prefix="¥"
            onChange={(value) => updatePlatformInput('otherCost', value)}
          />
        )}
        {!compactMode && (
          <NumberField
            label={t('pricingEngine.weight')}
            value={safeNumber(activeInputs.weight)}
            suffix="kg"
            onChange={(value) => updatePlatformInput('weight', value)}
          />
        )}
      </div>
      {!compactMode && (
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pricingEngine.dimensions')}</label>
          <div className={FIELD_ROW_TIGHT}>
            <input
              className="w-full px-2 py-2.5 text-center bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              placeholder="L"
              type="number"
              value={safeNumber(activeInputs.dims.l)}
            onChange={(e) => updateDimensions({ l: Number(e.target.value) })}
          />
            <input
              className="w-full px-2 py-2.5 text-center bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              placeholder="W"
              type="number"
              value={safeNumber(activeInputs.dims.w)}
            onChange={(e) => updateDimensions({ w: Number(e.target.value) })}
          />
            <input
              className="w-full px-2 py-2.5 text-center bg-white border border-slate-300 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all"
              placeholder="H"
              type="number"
              value={safeNumber(activeInputs.dims.h)}
            onChange={(e) => updateDimensions({ h: Number(e.target.value) })}
          />
          </div>
        </div>
      )}
      <div className="flex items-center justify-between gap-3">
        <div className="flex items-center gap-2 text-[11px] text-slate-500">
          <span>{t('pricingEngine.rateSource')}</span>
          {rateStatus === 'loading' && <span className="text-blue-500 font-semibold">{t('pricingEngine.rateLoading')}</span>}
          {rateStatus === 'error' && <span className="text-rose-500 font-semibold">{t('pricingEngine.rateError')}</span>}
          {rateStatus === 'idle' && rateUpdatedAt && (
            <span className="text-slate-400">{t('pricingEngine.rateUpdated').replace('{date}', rateUpdatedAt)}</span>
          )}
        </div>
        <div className="flex items-center gap-2">
          <button
            onClick={() =>
              setAutoRate((prev) => {
                const next = !prev;
                if (!next) {
                  setRateStatus('idle');
                }
                return next;
              })
            }
            className={`px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider ${
              autoRate ? 'bg-emerald-50 text-emerald-600' : 'bg-slate-100 text-slate-500'
            }`}
          >
            {autoRate ? t('pricingEngine.autoRateOn') : t('pricingEngine.autoRateOff')}
          </button>
          <button
            onClick={handleRefreshRate}
            className="px-2 py-1 rounded-md text-[10px] font-bold uppercase tracking-wider bg-slate-100 text-slate-600 hover:bg-slate-200 transition-colors"
          >
            {t('pricingEngine.refreshRate')}
          </button>
        </div>
      </div>
      <NumberField
        label={t('pricingEngine.exchangeRateLabel').replace('{currency}', activeCurrency.code)}
        value={safeNumber(activeInputs.exchangeRate)}
        onChange={(value) => updatePlatformInput('exchangeRate', value)}
        suffix={activeCurrency.code}
        disabled={autoRate}
        helper={autoRate ? t('pricingEngine.autoRateHint') : t('pricingEngine.manualRateHint')}
      />
    </div>
  );

  const renderStrategyCard = () => (
    <div className={`rounded-xl p-6 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest block mb-4">{t('pricingEngine.pricingStrategy')}</label>
      <div className="flex p-1 bg-slate-100 rounded-lg mb-4">
        <button
          onClick={() => setStrategyType('margin')}
          className={`flex-1 py-1.5 text-[11px] font-bold uppercase tracking-wider rounded-md transition-all ${
            strategyType === 'margin' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t('pricingEngine.targetMargin')}
        </button>
        <button
          onClick={() => setStrategyType('profit')}
          className={`flex-1 py-1.5 text-[11px] font-bold uppercase tracking-wider transition-all rounded-md ${
            strategyType === 'profit' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
          }`}
        >
          {t('pricingEngine.targetProfit')}
        </button>
      </div>
      {strategyType === 'margin' ? (
        <>
          <div className="flex items-center justify-between mb-4">
            <span className="text-sm font-semibold text-slate-700">{t('pricingEngine.targetValue')}</span>
            <div className="relative w-28">
              <input
                className="w-full px-4 py-1.5 text-right pr-8 font-bold text-primary border border-slate-200 rounded-lg focus:border-primary outline-none transition-all"
                type="number"
                value={safeNumber(activeInputs.targetMargin)}
                onChange={(e) => updatePlatformInput('targetMargin', clampNumber(Number(e.target.value), 0, maxMarginPercent))}
              />
              <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 font-bold text-xs">%</span>
            </div>
          </div>
          <div className="relative pt-2">
            <input
              className="w-full relative z-10 h-1.5 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-primary"
              max={maxMarginPercent}
              min="0"
              step="1"
              type="range"
              value={safeNumber(activeInputs.targetMargin)}
              onChange={(e) => updatePlatformInput('targetMargin', Number(e.target.value))}
            />
          </div>
          <div className="flex justify-between text-[10px] font-bold text-slate-400 uppercase tracking-tighter mt-1">
            <span>0%</span>
            <span>{maxMarginPercent.toFixed(1)}%</span>
          </div>
        </>
      ) : (
        <NumberField
          label={t('pricingEngine.targetValue')}
          value={safeNumber(activeInputs.targetProfit)}
          prefix="¥"
          onChange={(value) => updatePlatformInput('targetProfit', value)}
        />
      )}
    </div>
  );

  const renderSummaryMetrics = () => {
    const metrics = [
      { label: t('pricingEngine.platformFeeTotal'), value: `${activeCurrency.symbol}${fmt(calculations.platformFeeTotal)}` },
      { label: t('pricingEngine.baseCostTotal'), value: `${activeCurrency.symbol}${fmt(calculations.baseCostTotal)}` },
      { label: t('pricingEngine.totalCost'), value: `${activeCurrency.symbol}${fmt(calculations.totalCost)}` },
    ];

    if (activePlatform === 'tiktok') {
      return (
        <div className="space-y-3">
          {metrics.map((item) => (
            <div key={item.label} className="flex items-center justify-between text-sm">
              <span className="text-white/70">{item.label}</span>
              <span className="font-semibold">{item.value}</span>
            </div>
          ))}
        </div>
      );
    }

    if (activePlatform === 'temu') {
      return (
        <div className="space-y-2">
          {metrics.map((item) => (
            <div key={item.label} className="flex items-center justify-between text-sm bg-white/10 rounded-lg px-3 py-2">
              <span className="text-white/70">{item.label}</span>
              <span className="font-semibold">{item.value}</span>
            </div>
          ))}
        </div>
      );
    }

    if (activePlatform === 'amazon') {
      return (
        <div className="grid grid-cols-3 gap-4">
          {metrics.map((item) => (
            <div key={item.label} className="bg-white/10 rounded-lg px-3 py-2">
              <div className="text-[10px] text-white/70 uppercase tracking-wide">{item.label}</div>
              <div className="text-sm font-semibold mt-1">{item.value}</div>
            </div>
          ))}
        </div>
      );
    }

    if (activePlatform === 'aliexpress') {
      return (
        <div className="grid grid-cols-3 gap-3">
          {metrics.map((item) => (
            <div key={item.label} className="bg-white/10 rounded-lg px-3 py-2">
              <div className="text-[10px] text-white/70 uppercase tracking-wide">{item.label}</div>
              <div className="text-sm font-semibold mt-1">{item.value}</div>
            </div>
          ))}
        </div>
      );
    }

    if (activePlatform === 'shopee') {
      return (
        <div className="grid grid-cols-1 gap-3">
          {metrics.map((item) => (
            <div key={item.label} className="bg-white/10 rounded-lg px-3 py-2 flex items-center justify-between">
              <span className="text-[10px] text-white/70 uppercase tracking-wide">{item.label}</span>
              <span className="text-sm font-semibold">{item.value}</span>
            </div>
          ))}
        </div>
      );
    }

    return (
      <div className="grid grid-cols-2 gap-4">
        {metrics.map((item) => (
          <div key={item.label} className="bg-white/10 rounded-lg px-3 py-2">
            <div className="text-[10px] text-white/70 uppercase tracking-wide">{item.label}</div>
            <div className="text-sm font-semibold mt-1">{item.value}</div>
          </div>
        ))}
      </div>
    );
  };

  const renderSummaryCard = () => (
    <div className={`${activeTheme.summary} rounded-xl p-8 text-white shadow-xl shadow-primary/20 relative overflow-hidden transition-all`}>
      <div className="absolute -right-10 -top-10 w-40 h-40 bg-white/10 rounded-full blur-3xl"></div>
      <div className="relative z-10 space-y-6">
        <div className="flex flex-col sm:flex-row justify-between items-start gap-4">
          <div>
            <h3 className={`text-xs font-medium uppercase tracking-widest mb-2 ${activeTheme.accent}`}>
              {t('pricingEngine.suggestedPrice')} · {t(PLATFORM_META[activePlatform].labelKey)}
            </h3>
            <div className="flex items-baseline flex-wrap gap-x-4 gap-y-1">
              <span className="text-5xl font-bold tracking-tight">
                {activeCurrency.symbol}{fmt(calculations.suggestedPrice)}
              </span>
              <div className="flex flex-col">
                <span className="text-blue-200 text-lg">≈ ¥{fmt(calculations.suggestedPrice / calculations.exchangeRate)} CNY</span>
                <span className="text-[10px] text-blue-100/60 font-medium whitespace-nowrap">
                  {t('pricingEngine.rate')
                    .replace('{rate}', calculations.exchangeRate.toFixed(4))
                    .replace('{currency}', activeCurrency.code)}
                </span>
              </div>
            </div>
            <div className="mt-4 text-[11px] text-white/70 font-semibold uppercase tracking-widest border-l-2 border-white/20 pl-3">
              {t('pricingEngine.breakEvenPrice').replace('{price}', `${activeCurrency.symbol}${fmt(calculations.breakEvenPrice)}`)}
            </div>
          </div>
          <div className={`px-3 py-1.5 rounded-lg text-[10px] font-bold uppercase tracking-wider shrink-0 shadow-lg ${activeTheme.badge}`}>
            {strategyType === 'margin'
              ? t('pricingEngine.optimizedMsg').replace('{value}', safeNumber(activeInputs.targetMargin).toString())
              : t('pricingEngine.optimizedProfit').replace('{value}', `¥${fmt(activeInputs.targetProfit)}`)}
          </div>
        </div>

        <div className="grid grid-cols-3 gap-4">
          <div>
            <div className="text-blue-100 text-xs mb-1">{t('pricingEngine.netProfit')}</div>
            <div className="text-xl font-bold">{activeCurrency.symbol}{fmt(calculations.profit)}</div>
            <div className="text-[10px] text-blue-200 mt-1">≈ ¥{fmt(calculations.profitCny)} CNY</div>
          </div>
          <div>
            <div className="text-blue-100 text-xs mb-1">{t('pricingEngine.profitMargin')}</div>
            <div className="text-xl font-bold">{(calculations.margin * 100).toFixed(1)}%</div>
            <div className="text-[10px] text-blue-200 mt-1 text-emerald-300 font-bold">{t('pricingEngine.targetMet')}</div>
          </div>
          <div>
            <div className="text-blue-100 text-xs mb-1">{t('pricingEngine.roi')}</div>
            <div className="text-xl font-bold">{(calculations.roi * 100).toFixed(1)}%</div>
            <div className="text-[10px] text-blue-200 mt-1">{t('pricingEngine.highMargin')}</div>
          </div>
        </div>

        {renderSummaryMetrics()}
      </div>
    </div>
  );

  const renderInsightCard = () => {
    const entries: { label: string; value: string }[] = [];
    if (activePlatform === 'amazon') {
      entries.push(
        { label: t('pricingEngine.sellingPlan'), value: activeInputs.sellingPlan === 'professional' ? t('pricingEngine.sellingPlanProfessional') : t('pricingEngine.sellingPlanIndividual') },
        { label: t('pricingEngine.fulfillmentMode'), value: activeInputs.fulfillmentMode === 'fbm' ? t('pricingEngine.fulfillmentFbm') : t('pricingEngine.fulfillmentFba') },
      );
    }
    if (activePlatform === 'tiktok') {
      const region = activeInputs.tiktokRegion ?? 'us';
      entries.push(
        { label: t('pricingEngine.tiktokRegion'), value: t(TIKTOK_REGION_PRESETS[region].labelKey) },
        { label: t('pricingEngine.affiliateRate'), value: `${(safeNumber(activeInputs.affiliateRate) * 100).toFixed(2)}%` },
        { label: t('pricingEngine.adsRate'), value: `${(safeNumber(activeInputs.adsRate) * 100).toFixed(2)}%` },
      );
    }
    if (activePlatform === 'ozon') {
      const categoryRate = OZON_CATEGORIES[activeInputs.categoryId ?? '15']?.rate ?? 0.15;
      entries.push(
        { label: t('pricingEngine.categoryLabel'), value: OZON_CATEGORIES[activeInputs.categoryId ?? '15']?.name ?? '—' },
        { label: t('pricingEngine.referralRateLabel'), value: `${(categoryRate * 100).toFixed(2)}%` },
      );
    }
    if (activePlatform === 'ebay') {
      entries.push(
        { label: t('pricingEngine.ebayFinalValueRate'), value: `${(safeNumber(activeInputs.ebayFinalValueRate) * 100).toFixed(2)}%` },
        { label: t('pricingEngine.ebayPerOrderFee'), value: `${activeCurrency.symbol}${fmt(safeNumber(activeInputs.ebayPerOrderFee))}` },
      );
    }
    if (activePlatform === 'walmart') {
      entries.push({ label: t('pricingEngine.referralRateLabel'), value: `${(safeNumber(activeInputs.referralRate) * 100).toFixed(2)}%` });
    }
    if (activePlatform === 'etsy') {
      entries.push(
        { label: t('pricingEngine.etsyListingFee'), value: `${activeCurrency.symbol}${fmt(safeNumber(activeInputs.etsyListingFee))}` },
        { label: t('pricingEngine.etsyTransactionFee'), value: `${(safeNumber(activeInputs.etsyTransactionRate) * 100).toFixed(2)}%` },
      );
    }
    if (activePlatform === 'temu') {
      entries.push(
        { label: t('pricingEngine.referralRateLabel'), value: `${(safeNumber(activeInputs.referralRate) * 100).toFixed(2)}%` },
        { label: t('pricingEngine.serviceFeeRate'), value: `${(safeNumber(activeInputs.serviceFeeRate) * 100).toFixed(2)}%` },
      );
    }
    if (activePlatform === 'aliexpress') {
      const category = ALIEXPRESS_CATEGORIES.find((item) => item.id === activeInputs.categoryId);
      entries.push(
        { label: t('pricingEngine.categoryLabel'), value: category?.name ?? '—' },
        { label: t('pricingEngine.referralRateLabel'), value: `${(safeNumber(activeInputs.referralRate) * 100).toFixed(2)}%` },
        { label: t('pricingEngine.transactionFeeRate'), value: `${(safeNumber(activeInputs.transactionRate) * 100).toFixed(2)}%` },
      );
    }
    if (activePlatform === 'shopee') {
      entries.push(
        { label: t('pricingEngine.referralRateLabel'), value: `${(safeNumber(activeInputs.referralRate) * 100).toFixed(2)}%` },
        { label: t('pricingEngine.transactionFeeRate'), value: `${(safeNumber(activeInputs.transactionRate) * 100).toFixed(2)}%` },
        { label: t('pricingEngine.serviceFeeRate'), value: `${(safeNumber(activeInputs.serviceFeeRate) * 100).toFixed(2)}%` },
      );
    }

    if (entries.length === 0) {
      return null;
    }

    return (
      <div className={`rounded-xl p-5 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
        <div className="flex items-center justify-between mb-3">
          <h3 className="font-bold text-slate-800">{t('pricingEngine.platformInsights')}</h3>
          <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{t(PLATFORM_META[activePlatform].labelKey)}</span>
        </div>
        <div className="grid grid-cols-[repeat(auto-fit,minmax(160px,1fr))] gap-3">
          {entries.map((item) => (
            <div key={item.label} className={`rounded-lg px-3 py-2 ${activeTheme.cardMuted} ${activeTheme.cardBorder}`}>
              <div className="text-[10px] text-slate-400 uppercase tracking-wide">{item.label}</div>
              <div className="text-sm font-semibold text-slate-800 mt-1">{item.value}</div>
            </div>
          ))}
        </div>
      </div>
    );
  };

  const renderBreakdownCard = () => (
    <div className={`rounded-xl overflow-hidden shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className={`px-6 py-4 border-b ${activeTheme.cardBorder} ${activeTheme.cardHeader} flex justify-between items-center`}>
        <div className="flex items-center gap-3">
          <div className="w-6 h-6 bg-primary/10 rounded-full flex items-center justify-center p-1 text-primary">
            {PLATFORM_META[activePlatform].icon}
          </div>
          <h3 className="font-bold text-slate-800">{t('pricingEngine.costBreakdown')}</h3>
        </div>
        <span className="text-[10px] font-bold text-slate-400 uppercase tracking-wider">{activeCurrency.code}</span>
      </div>
      <div className="p-6 space-y-4">
        {calculations.totalCostItems.map((item, index) => (
          <div
            key={`${item.label}-${index}`}
            className={`flex justify-between items-center py-2 ${item.variant === 'total' ? 'border-t border-slate-100 pt-4 mt-4 font-bold' : ''}`}
          >
            <div className="flex items-center gap-3">
              {item.variant === 'fee' && <Percent className="text-slate-400" />}
              {item.variant === 'cost' && <LocalShipping className="text-slate-400" />}
              {item.variant === 'total' && <Analytics className="text-primary" />}
              <span className="text-sm font-medium text-slate-600">{item.label}</span>
            </div>
            <span className="text-sm font-semibold text-slate-900">{activeCurrency.symbol}{fmt(item.amount)}</span>
          </div>
        ))}
      </div>
    </div>
  );

  const handleGenerateShare = () => {
    const canvas = document.createElement('canvas');
    canvas.width = 1200;
    canvas.height = 630;
    const ctx = canvas.getContext('2d');
    if (!ctx) {
      setToast({ show: true, title: t('pricingEngine.shareError'), msg: t('pricingEngine.shareErrorDesc') });
      setTimeout(() => setToast(null), 4000);
      return;
    }

    const palette = PLATFORM_SHARE_COLORS[activePlatform];
    const gradient = ctx.createLinearGradient(0, 0, canvas.width, canvas.height);
    gradient.addColorStop(0, palette.from);
    gradient.addColorStop(1, palette.to);
    ctx.fillStyle = gradient;
    ctx.fillRect(0, 0, canvas.width, canvas.height);

    ctx.fillStyle = 'rgba(255,255,255,0.1)';
    ctx.fillRect(60, 70, canvas.width - 120, canvas.height - 140);

    ctx.fillStyle = '#ffffff';
    ctx.font = '700 40px Inter, sans-serif';
    ctx.fillText('OneClickSell AI Pricing', 90, 150);

    ctx.font = '600 32px Inter, sans-serif';
    ctx.fillText(t(PLATFORM_META[activePlatform].labelKey), 90, 210);

    ctx.font = '700 80px Inter, sans-serif';
    ctx.fillText(`${activeCurrency.symbol}${fmt(calculations.suggestedPrice)}`, 90, 320);

    ctx.fillStyle = palette.accent;
    ctx.font = '600 32px Inter, sans-serif';
    ctx.fillText(`${t('pricingEngine.netProfit')}: ${activeCurrency.symbol}${fmt(calculations.profit)}`, 90, 380);

    ctx.fillStyle = '#ffffff';
    ctx.font = '500 28px Inter, sans-serif';
    ctx.fillText(`${t('pricingEngine.profitMargin')}: ${(calculations.margin * 100).toFixed(1)}%`, 90, 430);
    ctx.fillText(`${t('pricingEngine.roi')}: ${(calculations.roi * 100).toFixed(1)}%`, 90, 470);

    ctx.font = '500 22px Inter, sans-serif';
    ctx.fillText(`${t('pricingEngine.baseCostTotal')}: ${activeCurrency.symbol}${fmt(calculations.baseCostTotal)}`, 90, 520);
    ctx.fillText(`${t('pricingEngine.platformFeeTotal')}: ${activeCurrency.symbol}${fmt(calculations.platformFeeTotal)}`, 90, 555);

    const link = document.createElement('a');
    link.href = canvas.toDataURL('image/png');
    link.download = `pricing-${activePlatform}-${new Date().toISOString().slice(0, 10)}.png`;
    link.click();

    setToast({
      show: true,
      title: t('pricingEngine.shareReady'),
      msg: t('pricingEngine.shareReadyDesc').replace('{platform}', t(PLATFORM_META[activePlatform].labelKey)),
    });
    setTimeout(() => setToast(null), 4000);
  };

  const renderActions = () => (
    <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
      <button
        onClick={handleGenerateShare}
        className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold py-3.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm shadow-sm active:scale-95"
      >
        <PhotoCamera style={{ fontSize: 20 }} />
        {t('pricingEngine.shareImage')}
      </button>
      <button className="bg-white border border-slate-200 hover:bg-slate-50 text-slate-700 font-semibold py-3.5 px-6 rounded-lg transition-colors flex items-center justify-center gap-2 text-sm shadow-sm active:scale-95">
        <Download style={{ fontSize: 20 }} />
        {t('pricingEngine.exportReport')}
      </button>
    </div>
  );

  const renderAmazonFees = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.platformFees')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.amazon')}</span>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pricingEngine.categoryLabel')}</label>
        <div className="relative">
          <GridView className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
          <select
            value={activeInputs.categoryId}
            onChange={(e) => handleAmazonCategoryChange(e.target.value)}
            className="w-full px-4 py-2.5 pl-11 pr-10 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer"
          >
            {AMAZON_CATEGORIES.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} ({(item.rate * 100).toFixed(0)}%)
              </option>
            ))}
          </select>
          <ExpandMore className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.referralRateLabel')}
          value={Number((safeNumber(activeInputs.referralRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('referralRate', value / 100)}
        />
        {!compactMode && (
          <NumberField
            label={t('pricingEngine.referralMinFeeLabel')}
            value={safeNumber(activeInputs.referralMinFee)}
            prefix={activeCurrency.symbol}
            onChange={(value) => updatePlatformInput('referralMinFee', value)}
          />
        )}
      </div>
      <div className={FIELD_ROW_CLASS}>
        <div>
          <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pricingEngine.sellingPlan')}</label>
          <div className="flex h-[42px] p-1 bg-slate-100 rounded-lg">
            <button
              onClick={() => {
                updatePlatformInput('sellingPlan', 'individual');
                updatePlatformInput('sellingPlanFee', 0.99);
              }}
              className={`flex-1 text-xs font-bold rounded-md transition-all ${
                activeInputs.sellingPlan === 'individual' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('pricingEngine.sellingPlanIndividual')}
            </button>
            <button
              onClick={() => {
                updatePlatformInput('sellingPlan', 'professional');
                updatePlatformInput('sellingPlanFee', 0);
              }}
              className={`flex-1 text-xs font-bold rounded-md transition-all ${
                activeInputs.sellingPlan === 'professional' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
              }`}
            >
              {t('pricingEngine.sellingPlanProfessional')}
            </button>
          </div>
        </div>
        {!compactMode && (
          <NumberField
            label={t('pricingEngine.sellingPlanFee')}
            value={safeNumber(activeInputs.sellingPlanFee)}
            prefix={activeCurrency.symbol}
            onChange={(value) => updatePlatformInput('sellingPlanFee', value)}
          />
        )}
      </div>
      {!compactMode && (
        <>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pricingEngine.fulfillmentMode')}</label>
            <div className="flex h-[42px] p-1 bg-slate-100 rounded-lg">
              <button
                onClick={() => updatePlatformInput('fulfillmentMode', 'fba')}
                className={`flex-1 text-xs font-bold rounded-md transition-all ${
                  activeInputs.fulfillmentMode === 'fba' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t('pricingEngine.fulfillmentFba')}
              </button>
              <button
                onClick={() => updatePlatformInput('fulfillmentMode', 'fbm')}
                className={`flex-1 text-xs font-bold rounded-md transition-all ${
                  activeInputs.fulfillmentMode === 'fbm' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t('pricingEngine.fulfillmentFbm')}
              </button>
            </div>
          </div>
          {activeInputs.fulfillmentMode === 'fba' ? (
            <div className={FIELD_ROW_CLASS}>
              <NumberField
                label={t('pricingEngine.fulfillmentFee')}
                value={safeNumber(activeInputs.fulfillmentFee)}
                prefix={activeCurrency.symbol}
                onChange={(value) => updatePlatformInput('fulfillmentFee', value)}
              />
              <NumberField
                label={t('pricingEngine.storageFee')}
                value={safeNumber(activeInputs.storageFee)}
                prefix={activeCurrency.symbol}
                onChange={(value) => updatePlatformInput('storageFee', value)}
              />
              <NumberField
                label={t('pricingEngine.inboundShippingFee')}
                value={safeNumber(activeInputs.inboundShippingFee)}
                prefix={activeCurrency.symbol}
                onChange={(value) => updatePlatformInput('inboundShippingFee', value)}
              />
            </div>
          ) : (
            <NumberField
              label={t('pricingEngine.fbmHandlingFee')}
              value={safeNumber(activeInputs.fbmHandlingFee)}
              prefix={activeCurrency.symbol}
              onChange={(value) => updatePlatformInput('fbmHandlingFee', value)}
            />
          )}
          <NumberField
            label={t('pricingEngine.closingFeeLabel')}
            value={safeNumber(activeInputs.closingFee)}
            prefix={activeCurrency.symbol}
            onChange={(value) => updatePlatformInput('closingFee', value)}
          />
        </>
      )}
      <p className="text-[11px] text-slate-500">{t('pricingEngine.platformNotesAmazon')}</p>
    </div>
  );

  const renderTikTokRegionCard = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.tiktokRegion')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.tiktok')}</span>
      </div>
      <div className="relative">
        <select
          value={activeInputs.tiktokRegion ?? 'us'}
          onChange={(e) => handleTikTokRegionChange(e.target.value as TikTokRegion)}
          className="w-full px-4 py-2.5 pr-10 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer text-slate-900 font-medium"
        >
          {Object.entries(TIKTOK_REGION_PRESETS).map(([key, preset]) => (
            <option key={key} value={key}>
              {t(preset.labelKey)}
            </option>
          ))}
        </select>
        <ExpandMore className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
      </div>
      <p className="text-[11px] text-slate-500">{t('pricingEngine.tiktokRegionHint')}</p>
    </div>
  );

  const renderTikTokFees = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.platformFees')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.tiktok')}</span>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pricingEngine.categoryLabel')}</label>
        <div className="relative">
          <GridView className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
          <select
            value={activeInputs.categoryId}
            onChange={(e) => handleTikTokCategoryChange(e.target.value)}
            className="w-full px-4 py-2.5 pl-11 pr-10 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer"
          >
            {TIKTOK_CATEGORIES.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} ({(item.rate * 100).toFixed(0)}%)
              </option>
            ))}
          </select>
          <ExpandMore className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.referralRateLabel')}
          value={Number((safeNumber(activeInputs.referralRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('referralRate', value / 100)}
        />
        <NumberField
          label={t('pricingEngine.transactionFeeRate')}
          value={Number((safeNumber(activeInputs.transactionRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('transactionRate', value / 100)}
        />
      </div>
      {!compactMode && (
        <>
          <NumberField
            label={t('pricingEngine.transactionFeeFixed')}
            value={safeNumber(activeInputs.transactionFixed)}
            prefix={activeCurrency.symbol}
            onChange={(value) => updatePlatformInput('transactionFixed', value)}
          />
          <div className={FIELD_ROW_CLASS}>
            <NumberField
              label={t('pricingEngine.platformDiscount')}
              value={safeNumber(activeInputs.platformDiscount)}
              prefix={activeCurrency.symbol}
              onChange={(value) => updatePlatformInput('platformDiscount', value)}
            />
            <NumberField
              label={t('pricingEngine.tax')}
              value={safeNumber(activeInputs.tax)}
              prefix={activeCurrency.symbol}
              onChange={(value) => updatePlatformInput('tax', value)}
            />
          </div>
          <div>
            <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pricingEngine.shippingType')}</label>
            <div className="flex h-[42px] p-1 bg-slate-100 rounded-lg">
              <button
                onClick={() => updatePlatformInput('tiktokShippingType', 'platform')}
                className={`flex-1 text-xs font-bold rounded-md transition-all ${
                  activeInputs.tiktokShippingType === 'platform' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t('pricingEngine.shippingPlatform')}
              </button>
              <button
                onClick={() => updatePlatformInput('tiktokShippingType', 'seller')}
                className={`flex-1 text-xs font-bold rounded-md transition-all ${
                  activeInputs.tiktokShippingType === 'seller' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t('pricingEngine.shippingSeller')}
              </button>
              <button
                onClick={() => updatePlatformInput('tiktokShippingType', 'fbt')}
                className={`flex-1 text-xs font-bold rounded-md transition-all ${
                  activeInputs.tiktokShippingType === 'fbt' ? 'bg-primary text-white shadow-sm' : 'text-slate-500 hover:text-slate-700'
                }`}
              >
                {t('pricingEngine.shippingFbt')}
              </button>
            </div>
          </div>
          {activeInputs.tiktokShippingType === 'fbt' && (
            <div className={FIELD_ROW_CLASS}>
              <NumberField
                label={t('pricingEngine.fbtFulfillmentFee')}
                value={safeNumber(activeInputs.fbtFulfillmentFee)}
                prefix={activeCurrency.symbol}
                onChange={(value) => updatePlatformInput('fbtFulfillmentFee', value)}
              />
              <NumberField
                label={t('pricingEngine.fbtStorageFee')}
                value={safeNumber(activeInputs.fbtStorageFee)}
                prefix={activeCurrency.symbol}
                onChange={(value) => updatePlatformInput('fbtStorageFee', value)}
              />
            </div>
          )}
        </>
      )}
      <p className="text-[11px] text-slate-500">{t('pricingEngine.platformNotesTikTok')}</p>
    </div>
  );

  const renderTikTokMarketing = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.marketingCosts')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.tiktok')}</span>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.sellerDiscount')}
          value={safeNumber(activeInputs.sellerDiscount)}
          prefix={activeCurrency.symbol}
          onChange={(value) => updatePlatformInput('sellerDiscount', value)}
        />
        <NumberField
          label={t('pricingEngine.adsRate')}
          value={Number((safeNumber(activeInputs.adsRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('adsRate', value / 100)}
        />
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.affiliateRate')}
          value={Number((safeNumber(activeInputs.affiliateRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('affiliateRate', value / 100)}
        />
        <NumberField
          label={t('pricingEngine.affiliatePartnerRate')}
          value={Number((safeNumber(activeInputs.affiliatePartnerRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('affiliatePartnerRate', value / 100)}
        />
      </div>
    </div>
  );

  const renderOzonFees = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.platformFees')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.ozon')}</span>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pricingEngine.categoryLabel')}</label>
        <div className="relative">
          <GridView className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
          <select
            value={activeInputs.categoryId}
            onChange={(e) => updatePlatformInput('categoryId', e.target.value)}
            className="w-full px-4 py-2.5 pl-11 pr-10 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer"
          >
            {Object.entries(OZON_CATEGORIES).map(([id, cat]) => (
              <option key={id} value={id}>
                {cat.name} ({(cat.rate * 100).toFixed(0)}%)
              </option>
            ))}
          </select>
          <ExpandMore className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>
      <NumberField
        label={t('pricingEngine.referralRateLabel')}
        value={(OZON_CATEGORIES[activeInputs.categoryId ?? '15']?.rate ?? 0.15) * 100}
        suffix="%"
        onChange={() => undefined}
        disabled
      />
      {!compactMode && (
        <NumberField
          label={t('pricingEngine.ozonPlacementFee')}
          value={safeNumber(activeInputs.ozonPlacementFee)}
          prefix={activeCurrency.symbol}
          onChange={(value) => updatePlatformInput('ozonPlacementFee', value)}
        />
      )}
      <p className="text-[11px] text-slate-500">{t('pricingEngine.platformNotesOzon')}</p>
    </div>
  );

  const renderEbayFees = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.platformFees')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.ebay')}</span>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.ebayFinalValueRate')}
          value={Number((safeNumber(activeInputs.ebayFinalValueRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('ebayFinalValueRate', value / 100)}
        />
        <NumberField
          label={t('pricingEngine.ebayPerOrderFee')}
          value={safeNumber(activeInputs.ebayPerOrderFee)}
          prefix={activeCurrency.symbol}
          onChange={(value) => updatePlatformInput('ebayPerOrderFee', value)}
        />
      </div>
      {!compactMode && (
        <>
          <div className={FIELD_ROW_CLASS}>
            <NumberField
              label={t('pricingEngine.ebayInsertionFee')}
              value={safeNumber(activeInputs.ebayInsertionFee)}
              prefix={activeCurrency.symbol}
              onChange={(value) => updatePlatformInput('ebayInsertionFee', value)}
            />
            <NumberField
              label={t('pricingEngine.ebayInternationalRate')}
              value={Number((safeNumber(activeInputs.ebayInternationalRate) * 100).toFixed(2))}
              suffix="%"
              onChange={(value) => updatePlatformInput('ebayInternationalRate', value / 100)}
            />
          </div>
          <NumberField
            label={t('pricingEngine.tax')}
            value={safeNumber(activeInputs.tax)}
            prefix={activeCurrency.symbol}
            onChange={(value) => updatePlatformInput('tax', value)}
          />
        </>
      )}
      <p className="text-[11px] text-slate-500">{t('pricingEngine.platformNotesEbay')}</p>
    </div>
  );

  const renderWalmartFees = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.platformFees')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.walmart')}</span>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pricingEngine.categoryLabel')}</label>
        <div className="relative">
          <GridView className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
          <select
            value={activeInputs.categoryId}
            onChange={(e) => handleWalmartCategoryChange(e.target.value)}
            className="w-full px-4 py-2.5 pl-11 pr-10 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer"
          >
            {WALMART_CATEGORIES.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} ({(item.rate * 100).toFixed(0)}%)
              </option>
            ))}
          </select>
          <ExpandMore className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>
      <NumberField
        label={t('pricingEngine.referralRateLabel')}
        value={Number((safeNumber(activeInputs.referralRate) * 100).toFixed(2))}
        suffix="%"
        onChange={(value) => updatePlatformInput('referralRate', value / 100)}
      />
      <p className="text-[11px] text-slate-500">{t('pricingEngine.platformNotesWalmart')}</p>
    </div>
  );

  const renderEtsyFees = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.platformFees')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.etsy')}</span>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.etsyListingFee')}
          value={safeNumber(activeInputs.etsyListingFee)}
          prefix={activeCurrency.symbol}
          onChange={(value) => updatePlatformInput('etsyListingFee', value)}
        />
        <NumberField
          label={t('pricingEngine.etsyTransactionFee')}
          value={Number((safeNumber(activeInputs.etsyTransactionRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('etsyTransactionRate', value / 100)}
        />
      </div>
      {!compactMode && (
        <>
          <div className={FIELD_ROW_CLASS}>
            <NumberField
              label={t('pricingEngine.etsyPaymentRate')}
              value={Number((safeNumber(activeInputs.etsyPaymentRate) * 100).toFixed(2))}
              suffix="%"
              onChange={(value) => updatePlatformInput('etsyPaymentRate', value / 100)}
            />
            <NumberField
              label={t('pricingEngine.etsyPaymentFixed')}
              value={safeNumber(activeInputs.etsyPaymentFixed)}
              prefix={activeCurrency.symbol}
              onChange={(value) => updatePlatformInput('etsyPaymentFixed', value)}
            />
          </div>
          <div className={FIELD_ROW_CLASS}>
            <NumberField
              label={t('pricingEngine.etsyOffsiteAdsRate')}
              value={Number((safeNumber(activeInputs.etsyOffsiteAdsRate) * 100).toFixed(2))}
              suffix="%"
              onChange={(value) => updatePlatformInput('etsyOffsiteAdsRate', value / 100)}
            />
            <NumberField
              label={t('pricingEngine.tax')}
              value={safeNumber(activeInputs.tax)}
              prefix={activeCurrency.symbol}
              onChange={(value) => updatePlatformInput('tax', value)}
            />
          </div>
        </>
      )}
      <p className="text-[11px] text-slate-500">{t('pricingEngine.platformNotesEtsy')}</p>
    </div>
  );

  const renderTemuFees = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.platformFees')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.temu')}</span>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.referralRateLabel')}
          value={Number((safeNumber(activeInputs.referralRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('referralRate', value / 100)}
        />
        <NumberField
          label={t('pricingEngine.serviceFeeRate')}
          value={Number((safeNumber(activeInputs.serviceFeeRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('serviceFeeRate', value / 100)}
        />
      </div>
      {!compactMode && (
        <div className={FIELD_ROW_CLASS}>
          <NumberField
            label={t('pricingEngine.serviceFeeFixed')}
            value={safeNumber(activeInputs.serviceFeeFixed)}
            prefix={activeCurrency.symbol}
            onChange={(value) => updatePlatformInput('serviceFeeFixed', value)}
          />
          <NumberField
            label={t('pricingEngine.fulfillmentFee')}
            value={safeNumber(activeInputs.fulfillmentFee)}
            prefix={activeCurrency.symbol}
            onChange={(value) => updatePlatformInput('fulfillmentFee', value)}
          />
        </div>
      )}
      <p className="text-[11px] text-slate-500">{t('pricingEngine.platformNotesTemu')}</p>
    </div>
  );

  const renderTemuPrograms = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.marketingCosts')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.temuProgram')}</span>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.platformDiscount')}
          value={safeNumber(activeInputs.platformDiscount)}
          prefix={activeCurrency.symbol}
          onChange={(value) => updatePlatformInput('platformDiscount', value)}
        />
        <NumberField
          label={t('pricingEngine.adsRate')}
          value={Number((safeNumber(activeInputs.adsRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('adsRate', value / 100)}
        />
      </div>
      <NumberField
        label={t('pricingEngine.tax')}
        value={safeNumber(activeInputs.tax)}
        prefix={activeCurrency.symbol}
        onChange={(value) => updatePlatformInput('tax', value)}
      />
    </div>
  );

  const renderAliExpressFees = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.platformFees')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.aliexpress')}</span>
      </div>
      <div>
        <label className="block text-sm font-semibold text-slate-700 mb-2">{t('pricingEngine.categoryLabel')}</label>
        <div className="relative">
          <GridView className="absolute left-4 top-1/2 -translate-y-1/2 text-slate-400 text-xl" />
          <select
            value={activeInputs.categoryId}
            onChange={(e) => handleAliExpressCategoryChange(e.target.value)}
            className="w-full px-4 py-2.5 pl-11 pr-10 bg-white border border-slate-200 rounded-lg text-sm focus:ring-2 focus:ring-primary/20 focus:border-primary outline-none transition-all appearance-none cursor-pointer"
          >
            {ALIEXPRESS_CATEGORIES.map((item) => (
              <option key={item.id} value={item.id}>
                {item.name} ({(item.rate * 100).toFixed(0)}%)
              </option>
            ))}
          </select>
          <ExpandMore className="absolute right-4 top-1/2 -translate-y-1/2 text-slate-400 pointer-events-none" />
        </div>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.referralRateLabel')}
          value={Number((safeNumber(activeInputs.referralRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('referralRate', value / 100)}
        />
        {!compactMode && (
          <NumberField
            label={t('pricingEngine.transactionFeeRate')}
            value={Number((safeNumber(activeInputs.transactionRate) * 100).toFixed(2))}
            suffix="%"
            onChange={(value) => updatePlatformInput('transactionRate', value / 100)}
          />
        )}
      </div>
      {!compactMode && (
        <div className={FIELD_ROW_CLASS}>
          <NumberField
            label={t('pricingEngine.transactionFeeFixed')}
            value={safeNumber(activeInputs.transactionFixed)}
            prefix={activeCurrency.symbol}
            onChange={(value) => updatePlatformInput('transactionFixed', value)}
          />
          <NumberField
            label={t('pricingEngine.orderServiceFee')}
            value={safeNumber(activeInputs.serviceFeeFixed)}
            prefix={activeCurrency.symbol}
            onChange={(value) => updatePlatformInput('serviceFeeFixed', value)}
          />
        </div>
      )}
      <p className="text-[11px] text-slate-500">{t('pricingEngine.platformNotesAliExpress')}</p>
    </div>
  );

  const renderAliExpressPrograms = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.marketingCosts')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.aliexpressProgram')}</span>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.platformDiscount')}
          value={safeNumber(activeInputs.platformDiscount)}
          prefix={activeCurrency.symbol}
          onChange={(value) => updatePlatformInput('platformDiscount', value)}
        />
        <NumberField
          label={t('pricingEngine.adsRate')}
          value={Number((safeNumber(activeInputs.adsRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('adsRate', value / 100)}
        />
      </div>
      <NumberField
        label={t('pricingEngine.tax')}
        value={safeNumber(activeInputs.tax)}
        prefix={activeCurrency.symbol}
        onChange={(value) => updatePlatformInput('tax', value)}
      />
    </div>
  );

  const renderShopeeFees = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.platformFees')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.platforms.shopee')}</span>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.referralRateLabel')}
          value={Number((safeNumber(activeInputs.referralRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('referralRate', value / 100)}
        />
        <NumberField
          label={t('pricingEngine.transactionFeeRate')}
          value={Number((safeNumber(activeInputs.transactionRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('transactionRate', value / 100)}
        />
      </div>
      {!compactMode && (
        <>
          <div className={FIELD_ROW_CLASS}>
            <NumberField
              label={t('pricingEngine.transactionFeeFixed')}
              value={safeNumber(activeInputs.transactionFixed)}
              prefix={activeCurrency.symbol}
              onChange={(value) => updatePlatformInput('transactionFixed', value)}
            />
            <NumberField
              label={t('pricingEngine.serviceFeeRate')}
              value={Number((safeNumber(activeInputs.serviceFeeRate) * 100).toFixed(2))}
              suffix="%"
              onChange={(value) => updatePlatformInput('serviceFeeRate', value / 100)}
            />
          </div>
          <NumberField
            label={t('pricingEngine.platformSupportFee')}
            value={safeNumber(activeInputs.serviceFeeFixed)}
            prefix={activeCurrency.symbol}
            onChange={(value) => updatePlatformInput('serviceFeeFixed', value)}
            helper={t('pricingEngine.platformSupportFeeHint')}
          />
        </>
      )}
      <p className="text-[11px] text-slate-500">{t('pricingEngine.platformNotesShopee')}</p>
    </div>
  );

  const renderShopeePrograms = () => (
    <div className={`rounded-xl p-6 space-y-4 shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}>
      <div className="flex items-center justify-between">
        <label className="text-[11px] font-bold text-slate-400 uppercase tracking-widest">{t('pricingEngine.marketingCosts')}</label>
        <span className="text-[10px] text-slate-400 font-medium">{t('pricingEngine.shopeeProgram')}</span>
      </div>
      <div className={FIELD_ROW_CLASS}>
        <NumberField
          label={t('pricingEngine.shippingSubsidy')}
          value={safeNumber(activeInputs.shippingSubsidy)}
          prefix={activeCurrency.symbol}
          onChange={(value) => updatePlatformInput('shippingSubsidy', value)}
        />
        <NumberField
          label={t('pricingEngine.adsRate')}
          value={Number((safeNumber(activeInputs.adsRate) * 100).toFixed(2))}
          suffix="%"
          onChange={(value) => updatePlatformInput('adsRate', value / 100)}
        />
      </div>
      <NumberField
        label={t('pricingEngine.tax')}
        value={safeNumber(activeInputs.tax)}
        prefix={activeCurrency.symbol}
        onChange={(value) => updatePlatformInput('tax', value)}
      />
    </div>
  );

  const renderLeftColumn = () => {
    if (activePlatform === 'amazon') {
      return (
        <div className="lg:col-span-6 space-y-6">
          {renderPlatformSelector()}
          <div className="space-y-4">
            {renderBaseCostCard()}
            {renderAmazonFees()}
          </div>
          {renderStrategyCard()}
        </div>
      );
    }

    if (activePlatform === 'tiktok') {
      return (
        <div className="lg:col-span-7 space-y-6">
          {renderPlatformSelector()}
          <div className="space-y-4">
            {renderTikTokRegionCard()}
            {renderBaseCostCard()}
          </div>
          <div className="space-y-4">
            {renderTikTokFees()}
            {!compactMode && renderTikTokMarketing()}
          </div>
          {renderStrategyCard()}
        </div>
      );
    }

    if (activePlatform === 'ozon') {
      return (
        <div className="lg:col-span-5 space-y-6">
          {renderPlatformSelector()}
          {renderBaseCostCard()}
          {renderOzonFees()}
          {renderStrategyCard()}
        </div>
      );
    }

    if (activePlatform === 'temu') {
      return (
        <div className="lg:col-span-6 space-y-6">
          {renderPlatformSelector()}
          <div className="space-y-4">
            {renderBaseCostCard()}
            {renderTemuFees()}
          </div>
          {!compactMode && renderTemuPrograms()}
          {renderStrategyCard()}
        </div>
      );
    }

    if (activePlatform === 'aliexpress') {
      return (
        <div className="lg:col-span-7 space-y-6">
          {renderPlatformSelector()}
          {renderBaseCostCard()}
          <div className="space-y-4">
            {renderAliExpressFees()}
            {!compactMode && renderAliExpressPrograms()}
          </div>
          {renderStrategyCard()}
        </div>
      );
    }

    if (activePlatform === 'shopee') {
      return (
        <div className="lg:col-span-6 space-y-6">
          {renderPlatformSelector()}
          <div className="space-y-4">
            {renderBaseCostCard()}
            {renderShopeeFees()}
          </div>
          {!compactMode && renderShopeePrograms()}
          {renderStrategyCard()}
        </div>
      );
    }

    if (activePlatform === 'ebay') {
      return (
        <div className="lg:col-span-6 space-y-6">
          {renderPlatformSelector()}
          {renderBaseCostCard()}
          {renderEbayFees()}
          {renderStrategyCard()}
        </div>
      );
    }

    if (activePlatform === 'walmart') {
      return (
        <div className="lg:col-span-5 space-y-6">
          {renderPlatformSelector()}
          {renderBaseCostCard()}
          {renderWalmartFees()}
          {renderStrategyCard()}
        </div>
      );
    }

    return (
      <div className="lg:col-span-6 space-y-6">
        {renderPlatformSelector()}
        {renderBaseCostCard()}
        {renderEtsyFees()}
        {renderStrategyCard()}
      </div>
    );
  };

  const renderRightColumn = () => {
    if (activePlatform === 'tiktok') {
      return (
        <div className="lg:col-span-5 space-y-6">
          {renderSummaryCard()}
          {renderInsightCard()}
          {renderBreakdownCard()}
          {renderActions()}
        </div>
      );
    }

    if (activePlatform === 'ozon') {
      return (
        <div className="lg:col-span-7 space-y-6">
          {renderSummaryCard()}
          {renderInsightCard()}
          {renderBreakdownCard()}
          {renderActions()}
        </div>
      );
    }

    if (activePlatform === 'temu') {
      return (
        <div className="lg:col-span-6 space-y-6">
          {renderSummaryCard()}
          {renderInsightCard()}
          {renderBreakdownCard()}
          {renderActions()}
        </div>
      );
    }

    if (activePlatform === 'aliexpress') {
      return (
        <div className="lg:col-span-5 space-y-6">
          {renderSummaryCard()}
          {renderInsightCard()}
          {renderBreakdownCard()}
          {renderActions()}
        </div>
      );
    }

    if (activePlatform === 'shopee') {
      return (
        <div className="lg:col-span-6 space-y-6">
          {renderSummaryCard()}
          {renderInsightCard()}
          {renderBreakdownCard()}
          {renderActions()}
        </div>
      );
    }

    if (activePlatform === 'walmart') {
      return (
        <div className="lg:col-span-7 space-y-6">
          {renderSummaryCard()}
          {renderInsightCard()}
          {renderBreakdownCard()}
          {renderActions()}
        </div>
      );
    }

    return (
      <div className="lg:col-span-6 space-y-6">
        {renderSummaryCard()}
        {renderInsightCard()}
        {renderBreakdownCard()}
        {renderActions()}
      </div>
    );
  };

  const renderPlatformBrandPanel = () => {
    const brand = PLATFORM_BRAND_LOGOS[activePlatform];
    if (!brand) {
      return null;
    }
    const isWide = brand.layout === 'wide';
    const isOzon = activePlatform === 'ozon';
    const sizeClass = isWide ? 'w-[72%] h-[42%]' : 'w-[58%] h-[58%]';

    return (
      <div className="flex flex-col items-center justify-center w-[280px]">
        <div
          className={`relative w-full h-[360px] rounded-[32px] overflow-hidden shadow-sm ${activeTheme.card} ${activeTheme.cardBorder}`}
        >
          <div className="absolute inset-0 bg-gradient-to-br from-white/70 via-white/40 to-transparent" />
          <div className="absolute inset-0 opacity-40 mix-blend-multiply brand-glow" />
          <div className="relative z-10 flex items-center justify-center w-full h-full">
            {isOzon ? (
              <svg
                viewBox="0 0 320 320"
                className={`brand-float ${sizeClass} drop-shadow-xl`}
                role="img"
                aria-label="Ozon logo"
              >
                <defs>
                  <clipPath id="ozon-clip">
                    <rect x="0" y="0" width="320" height="320" rx="56" />
                  </clipPath>
                </defs>
                <g clipPath="url(#ozon-clip)">
                  <rect x="0" y="0" width="320" height="320" fill="#3B5BFF" />
                  <g transform="translate(-70 0) rotate(-25 160 160)" opacity="0.95">
                    <rect x="0" y="-40" width="70" height="420" fill="#FF2D93" />
                    <rect x="70" y="-40" width="70" height="420" fill="#2F6BFF" />
                    <rect x="140" y="-40" width="70" height="420" fill="#FF2D93" />
                    <rect x="210" y="-40" width="70" height="420" fill="#2F6BFF" />
                    <rect x="280" y="-40" width="70" height="420" fill="#FF2D93" />
                    <rect x="350" y="-40" width="70" height="420" fill="#2F6BFF" />
                  </g>
                  <rect x="0" y="0" width="320" height="320" fill="rgba(0,0,0,0.08)" />
                  <text
                    x="50%"
                    y="55%"
                    textAnchor="middle"
                    fontSize="84"
                    fontWeight="900"
                    fontFamily="Inter, system-ui, sans-serif"
                    fill="#ffffff"
                    letterSpacing="6"
                  >
                    OZON
                  </text>
                </g>
              </svg>
            ) : (
              <img
                src={brand.src}
                alt={`${brand.alt} logo`}
                className={`brand-float ${sizeClass} object-contain drop-shadow-xl`}
                loading="lazy"
              />
            )}
          </div>
          <div className="absolute inset-0 pointer-events-none brand-shine" />
        </div>
      </div>
    );
  };

  return (
    <div className={`min-h-screen pb-32 relative ${activeTheme.page}`}>
      {toast && (
        <div className="fixed top-24 right-6 z-[100] bg-white border border-emerald-100 rounded-xl p-4 shadow-[0_10px_40px_-10px_rgba(0,0,0,0.08)] max-w-sm flex items-start gap-3 animate-in slide-in-from-right-4 duration-300">
          <div className="bg-emerald-50 rounded-full p-1.5 flex-shrink-0 text-emerald-600">
            <Check fontSize="small" className="font-bold" />
          </div>
          <div className="flex-1 pt-0.5">
            <h4 className="text-sm font-bold text-slate-900 leading-tight">{toast.title}</h4>
            <p className="text-xs text-slate-500 mt-1 leading-relaxed">{toast.msg}</p>
          </div>
          <button onClick={() => setToast(null)} className="text-slate-400 hover:text-slate-600 transition-colors -mt-1">
            <Close fontSize="small" />
          </button>
        </div>
      )}

      <main className="max-w-6xl mx-auto px-6 py-10 relative">
        <style>{`
          @keyframes brand-float {
            0%, 100% { transform: translateY(0) scale(1); }
            50% { transform: translateY(-10px) scale(1.02); }
          }
          @keyframes brand-glow {
            0%, 100% { opacity: 0.15; }
            50% { opacity: 0.45; }
          }
          @keyframes brand-shine {
            0% { transform: translateX(-120%); opacity: 0; }
            40% { opacity: 0.35; }
            100% { transform: translateX(120%); opacity: 0; }
          }
          .brand-float { animation: brand-float 6s ease-in-out infinite; }
          .brand-glow { background: radial-gradient(circle at 30% 30%, rgba(255,255,255,0.6), transparent 60%); animation: brand-glow 5s ease-in-out infinite; }
          .brand-shine { background: linear-gradient(110deg, transparent 0%, rgba(255,255,255,0.6) 50%, transparent 100%); animation: brand-shine 6s ease-in-out infinite; }
        `}</style>
        <div className="relative z-10">
          <div className="hidden xl:block absolute -left-[340px] top-6">
            {renderPlatformBrandPanel()}
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
            {renderLeftColumn()}
            {renderRightColumn()}
          </div>
        </div>
      </main>
    </div>
  );
};
